/**
 * Every Firestore path in the application, in one place.
 *
 * A path spelled inline in a component is a path the security rules were never
 * written for and the emulator tests never covered. These functions are the only
 * legal way to name a collection or a document — the ESLint rule in
 * `app/eslint.config.js` fails a raw `collection(db, '...')` string.
 *
 * The shape is deliberate:
 *
 *   users/{uid}                                  a person, across all tenants
 *   tenants/{tenantId}                           the tenant — a kennel, today
 *   tenants/{tenantId}/members/{uid}             the role, read by every rule
 *   tenants/{tenantId}/animals/{animalId}        adults — sires, dams, retired
 *   tenants/{tenantId}/litters/{litterId}
 *   tenants/{tenantId}/offspring/{offspringId}   litterId is a *field*, not a parent
 *   tenants/{tenantId}/offspring/{offspringId}/events/{id}
 *   tenants/{tenantId}/measurements/{id}          one WEIGHING of one litter
 *   tenants/{tenantId}/photoSessions/{id}         one PHOTOGRAPHING of one litter
 *   tenants/{tenantId}/tasks/{id}                 one THING TO DO to one litter
 *   invites/{inviteId}                           top level: read before joining
 *
 * The vocabulary is deliberately species-neutral — tenant, animal, offspring,
 * not kennel, dog, puppy. The product is a dog breeder's tool and the copy says
 * so, but nothing in the data model needs to know that, and a stud farm or a
 * cattery would be a second phrase tree rather than a second schema. Words the
 * whole animal world already shares — litter, sire, dam, vet — stay.
 *
 * Offspring hang off the tenant rather than off their litter because the home
 * screen is "every newborn I am currently raising", which under a litter parent
 * is a collection-group query — and a collection-group query cannot be scoped to
 * one tenant without a redundant `tenantId` field on every document anyway. One
 * flat collection per tenant answers it with a single indexed read, and offline
 * it is one cached collection rather than one per litter.
 */

export const users = () => 'users' as const;
export const user = (uid: string) => `users/${uid}`;

export const tenants = () => 'tenants' as const;
export const tenant = (tenantId: string) => `tenants/${tenantId}`;

export const members = (tenantId: string) => `tenants/${tenantId}/members`;
export const member = (tenantId: string, uid: string) => `tenants/${tenantId}/members/${uid}`;

export const animals = (tenantId: string) => `tenants/${tenantId}/animals`;
export const animal = (tenantId: string, animalId: string) =>
  `tenants/${tenantId}/animals/${animalId}`;

export const litters = (tenantId: string) => `tenants/${tenantId}/litters`;
export const litter = (tenantId: string, litterId: string) =>
  `tenants/${tenantId}/litters/${litterId}`;

/**
 * The one pair that breaks the plural/singular rhythm above: "offspring" is its
 * own plural, so the document accessor is named for how it is addressed rather
 * than by dropping an `s` it does not have.
 */
export const offspring = (tenantId: string) => `tenants/${tenantId}/offspring`;
export const offspringById = (tenantId: string, offspringId: string) =>
  `tenants/${tenantId}/offspring/${offspringId}`;

/**
 * Weighings, one document per SESSION — not per animal.
 *
 * A sibling of `offspring` rather than a subcollection under it, and for the
 * same reason `offspring` is a sibling of `litters`: the thing being stored is a
 * weighing of a whole litter, its `litterId` is a field, and one indexed query
 * per litter answers the chart, the table and the log at once. Under the animal
 * it was eight collections, eight listeners, and no document to attach "who
 * weighed them and when" to. See `MeasurementEntry` in types/index.ts.
 */
export const measurements = (tenantId: string) => `tenants/${tenantId}/measurements`;
export const measurement = (tenantId: string, id: string) =>
  `tenants/${tenantId}/measurements/${id}`;

/**
 * Where weighings lived before that: one document per animal per weighing.
 *
 * ONLY THE MIGRATION MAY NAME THIS. It is here rather than spelled inline in
 * `scripts/migrations/` because the rule is that every path in this project is
 * in this file, and a legacy path is still a path — the migration that reads it
 * is the one piece of code that most needs to be reviewed against the shape it
 * expects. `firestore.rules` no longer mentions it, so no client can read it
 * either way; what is left after the migration has run is nothing.
 */
export const legacyOffspringMeasurements = (tenantId: string, offspringId: string) =>
  `tenants/${tenantId}/offspring/${offspringId}/measurements`;

/**
 * Photographings, one document per SESSION — the same shape, and for the same
 * reasons, as `measurements` above.
 *
 * A litter is photographed the way it is weighed: in one pass, on one afternoon,
 * with a few frames of the whole nest and a few of each puppy. So the session is
 * the document, `litterId` is a field on it, and one indexed query per litter
 * answers the archive. See `PhotoSession` in types/index.ts.
 *
 * `photoSessions` and not `photos`, deliberately: `tenantPhoto` below is a
 * STORAGE path under `tenants/{t}/photos/`, and two different services holding
 * two different things at the same-looking address is a confusion nobody reading
 * the rules file needs.
 */
export const photoSessions = (tenantId: string) => `tenants/${tenantId}/photoSessions`;
export const photoSession = (tenantId: string, id: string) =>
  `tenants/${tenantId}/photoSessions/${id}`;

/**
 * Things to do to this litter, one document per task.
 *
 * A sibling of `measurements` and `photoSessions`, with `litterId` as a field,
 * for the third time and the same reason: the open list, the history under it
 * and the count on the heading are one indexed query per litter —
 * `where(litterId) orderBy(dueAt)` — where a subcollection under the litter
 * would be a second listener for a screen that already has one.
 *
 * Species-neutral like everything else here, and general on purpose: a task is
 * "worm them on Saturday" or "call the vet about the small one", and the day it
 * is due is the only structure it has. See `Task` in types/index.ts.
 */
export const tasks = (tenantId: string) => `tenants/${tenantId}/tasks`;
export const task = (tenantId: string, id: string) => `tenants/${tenantId}/tasks/${id}`;

export const events = (tenantId: string, offspringId: string) =>
  `tenants/${tenantId}/offspring/${offspringId}/events`;
export const event = (tenantId: string, offspringId: string, id: string) =>
  `tenants/${tenantId}/offspring/${offspringId}/events/${id}`;

export const invites = () => 'invites' as const;
export const invite = (inviteId: string) => `invites/${inviteId}`;

/**
 * Storage, which has its own rules file and its own path grammar. Photographs
 * belong to the tenant, never to one animal alone: a puppy can be sold and its
 * picture still wanted in the litter's album.
 */
export const tenantPhoto = (tenantId: string, photoId: string) =>
  `tenants/${tenantId}/photos/${photoId}`;
